/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  Dropdown,
  FormGroup,
  PasswordInput,
  RadioButton,
  RadioButtonGroup,
  TextInput
} from '@carbon/react';
import cx from 'classnames';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import {
  CUSTOM_SERVICE_SEARCH_AUTH_TYPES,
  SEARCH_PROVIDER_TYPES
} from '../../../../ts/skill';
import { SslNotification } from '../../../ia/components/notifications/SslNotification';
import { REDACTED_FIELD } from '../../../ia/util/string';
import { HTTP_URL_REGEX } from '../../../ia/util/url';
import {
  setCustomServiceApiKey,
  setCustomServiceAuthType,
  setCustomServicePass,
  setCustomServiceUrl,
  setIsCustomServiceSetupValid,
  setCustomServiceUser,
  clearCustomServiceUser,
  clearCustomServicePass,
  clearCustomServiceApiKey,
  setIsDirty,
  setSelectedSearchType
} from '../../store/reducer';
import {
  selectDraftCustomServiceApiKey,
  selectDraftCustomServicePass,
  selectDraftCustomServiceUrl,
  selectDraftCustomServiceUser,
  selectCustomServiceAuthType,
  selectIsCustomServiceSetupValid,
  selectIsEditing,
  selectPreviouslySavedCustomServiceAuthType,
  selectCustomServiceSearchType
} from '../../store/selectors';
import { messages } from './messages';
import '../../../elastic-search/components/elastic-search-instance-config/ElasticSearchInstanceConfig.scss';

interface CustomServiceInstanceConfigProps {
  noHeader?: boolean;
  readOnly: boolean;
  onClose: () => void;
}

export const CustomServiceInstanceConfig = ({
  noHeader = false,
  readOnly,
  onClose
}: CustomServiceInstanceConfigProps) => {
  const dispatch = useDispatch();
  const formatMsg = useIntl().formatMessage;

  const isEditing = useSelector(selectIsEditing);
  const isCustomServiceSetupValid = useSelector(
    selectIsCustomServiceSetupValid
  );

  const url = useSelector(selectDraftCustomServiceUrl);
  const [invalidUrl, setInvalidUrl] = useState(false);
  const [invalidUrlText, setInvalidUrlText] = useState('');

  const authType = useSelector(selectCustomServiceAuthType);
  const previouslySavedElasticAuthType = useSelector(
    selectPreviouslySavedCustomServiceAuthType
  );

  const username = useSelector(selectDraftCustomServiceUser);
  const [invalidUsername, setInvalidUsername] = useState(false);
  const [invalidUsernameText, setInvalidUsernameText] = useState('');

  const password = useSelector(selectDraftCustomServicePass);
  const [invalidPassword, setInvalidPassword] = useState(false);
  const [invalidPasswordText, setInvalidPasswordText] = useState('');

  const apiKey = useSelector(selectDraftCustomServiceApiKey);
  const [invalidApiKey, setInvalidApiKey] = useState(false);
  const [invalidApiKeyText, setInvalidApiKeyText] = useState('');

  const [hidePasswordEye, setHidePasswordEye] = useState(
    isEditing &&
      previouslySavedElasticAuthType === CUSTOM_SERVICE_SEARCH_AUTH_TYPES.BASIC
  );
  const [hideApiKeyEye, setHideApiKeyEye] = useState(
    isEditing &&
      previouslySavedElasticAuthType === CUSTOM_SERVICE_SEARCH_AUTH_TYPES.API
  );

  const [searchProviderType, setSearchProviderType] = useState(
    useSelector(selectCustomServiceSearchType)
  );

  const urlChange = e => {
    const url = e?.target?.value ?? '';
    if (url === null || _.isEmpty(url?.trim())) {
      setInvalidUrl(true);
      setInvalidUrlText(formatMsg(messages.invalidEmptyValueText));
    } else if (!url.match(HTTP_URL_REGEX)) {
      setInvalidUrl(true);
      setInvalidUrlText(formatMsg(messages.invalidUrlValueText));
    } else {
      setInvalidUrl(false);
      setInvalidUrlText('');
    }
    dispatch(setCustomServiceUrl(url));
  };

  const usernameChange = e => {
    const username = e?.target?.value ?? '';
    if (username === null || _.isEmpty(username?.trim())) {
      setInvalidUsername(true);
      setInvalidUsernameText(formatMsg(messages.invalidEmptyValueText));
    } else {
      setInvalidUsername(false);
      setInvalidUsernameText('');
    }
    dispatch(setCustomServiceUser(username));
  };

  const passwordChange = e => {
    const password = e?.target?.value ?? '';
    if (password === null || _.isEmpty(password?.trim())) {
      setInvalidPassword(true);
      setInvalidPasswordText(formatMsg(messages.invalidEmptyValueText));
    } else {
      setInvalidPassword(false);
      setInvalidPasswordText('');
    }
    dispatch(setCustomServicePass(password));
    // Show the password button that says "show password" once the user has typing in the password field.
    setHidePasswordEye(false);
  };

  const apiKeyChange = e => {
    const apiKey = e?.target?.value ?? '';
    if (apiKey === null || _.isEmpty(apiKey?.trim())) {
      setInvalidApiKey(true);
      setInvalidApiKeyText(formatMsg(messages.invalidEmptyValueText));
    } else {
      setInvalidApiKey(false);
      setInvalidApiKeyText('');
    }
    dispatch(setCustomServiceApiKey(apiKey));
    // Show the button that says "show password" once the user has typing in the api key field.
    setHideApiKeyEye(false);
  };

  const changeAuthType = e => {
    const authType = e?.selectedItem;
    dispatch(setCustomServiceAuthType(authType.id));
    if (authType === CUSTOM_SERVICE_SEARCH_AUTH_TYPES.API) {
      dispatch(clearCustomServiceUser());
      dispatch(clearCustomServicePass());
    } else if (authType === CUSTOM_SERVICE_SEARCH_AUTH_TYPES.BASIC) {
      dispatch(clearCustomServiceApiKey());
    } else {
      dispatch(clearCustomServiceUser());
      dispatch(clearCustomServicePass());
      dispatch(clearCustomServiceApiKey());
      dispatch(setIsDirty(true));
    }
  };

  const handleSearchTypeSelection = (radioBtnValue: SEARCH_PROVIDER_TYPES) => {
    setSearchProviderType(radioBtnValue);
    dispatch(setSelectedSearchType(radioBtnValue));
  };

  const items = [
    {
      id: 'apikey',
      text: formatMsg(messages.authTypeAPIkeyText)
    },
    {
      id: 'basic',
      text: formatMsg(messages.authTypeBasicText)
    },
    {
      id: 'none',
      text: formatMsg(messages.authTypeNoneText)
    }
  ];

  useEffect(() => {
    if (searchProviderType === SEARCH_PROVIDER_TYPES.CLIENT_SIDE) {
      dispatch(setIsDirty(true));
    }
  }, [dispatch, searchProviderType]);

  // Update the parent panel on the validity of the inputs for the custom service config.
  useEffect(() => {
    if (
      invalidUrl ||
      (authType === CUSTOM_SERVICE_SEARCH_AUTH_TYPES.API && invalidApiKey) ||
      (authType === CUSTOM_SERVICE_SEARCH_AUTH_TYPES.BASIC &&
        (invalidUsername || invalidPassword))
    ) {
      dispatch(setIsCustomServiceSetupValid(false));
    } else {
      dispatch(setIsCustomServiceSetupValid(true));
    }
  }, [
    authType,
    dispatch,
    invalidApiKey,
    invalidPassword,
    invalidUrl,
    invalidUsername
  ]);

  return (
    <>
      <div className="CustomServiceSearchInstanceConfig">
        <div
          className={cx(
            'CustomServiceSearchInstanceConfig__content-container',
            {
              'CustomServiceSearchInstanceConfig__content-container-no-padding':
                noHeader
            }
          )}>
          {!noHeader && (
            <div className="CustomServiceSearchInstanceConfig__header">
              <FormattedMessage {...messages.customServiceDescription} />
            </div>
          )}
          <div
            className={cx(
              'CustomServiceSearchInstanceConfig__radio-button-group',
              {
                'CustomServiceSearchInstanceConfig__radio-button-padding':
                  noHeader
              }
            )}>
            <RadioButtonGroup
              legendText={!noHeader ? '' : formatMsg(messages.serviceType)}
              name="custom-service-search-type-radio-btn-group"
              valueSelected={searchProviderType}
              onChange={handleSearchTypeSelection}>
              <RadioButton
                labelText={formatMsg(messages.serverSideSearch)}
                id={SEARCH_PROVIDER_TYPES.SERVER_SIDE}
                value={SEARCH_PROVIDER_TYPES.SERVER_SIDE}
              />
              <RadioButton
                labelText={formatMsg(messages.clientSideSearch)}
                id={SEARCH_PROVIDER_TYPES.CLIENT_SIDE}
                value={SEARCH_PROVIDER_TYPES.CLIENT_SIDE}
              />
            </RadioButtonGroup>
          </div>
          {searchProviderType === SEARCH_PROVIDER_TYPES.SERVER_SIDE && (
            <FormGroup
              legendText=""
              className="CustomServiceSearchInstanceConfig__form">
              <div
                className={cx('', {
                  ElasticSearchInstanceConfig__URL: isCustomServiceSetupValid
                })}>
                <TextInput
                  autoComplete="off"
                  id="CustomServiceSearchInstanceConfig__form-url-input"
                  invalid={invalidUrl}
                  invalidText={invalidUrlText}
                  placeholder={formatMsg(messages.urlPlaceholder)}
                  readOnly={readOnly}
                  labelText={formatMsg(messages.urlLabelText)}
                  onChange={urlChange}
                  size="md"
                  value={url}
                  type="text"
                />
              </div>
              <SslNotification callback={onClose} />
              <br />
              <br />
              <Dropdown
                id="CustomServiceSearchInstanceConfig__form-auth-type-dropdown"
                label={formatMsg(messages.authTypeLabelText)}
                titleText={formatMsg(messages.authTypeLabelText)}
                items={items}
                itemToString={item => item.text}
                selectedItem={items.find(item => {
                  return item.id === authType;
                })}
                onChange={changeAuthType}></Dropdown>
              {authType === CUSTOM_SERVICE_SEARCH_AUTH_TYPES.BASIC && (
                <>
                  <TextInput
                    autoComplete="off"
                    id="CustomServiceSearchInstanceConfig__form-username-input"
                    invalid={invalidUsername}
                    invalidText={invalidUsernameText}
                    placeholder={formatMsg(messages.usernamePlaceholder)}
                    readOnly={readOnly}
                    labelText={formatMsg(messages.usernameLabelText)}
                    onChange={usernameChange}
                    size="md"
                    value={username}
                    type="text"
                  />
                  <PasswordInput
                    className={cx({
                      redactedField: hidePasswordEye
                    })}
                    autoComplete="new-password"
                    id="CustomServiceSearchInstanceConfig__form-password-input"
                    invalid={invalidPassword}
                    invalidText={invalidPasswordText}
                    placeholder={REDACTED_FIELD}
                    readOnly={readOnly}
                    labelText={formatMsg(messages.passwordLabelText)}
                    onChange={passwordChange}
                    size="md"
                    value={password}
                  />
                </>
              )}
              {authType === CUSTOM_SERVICE_SEARCH_AUTH_TYPES.API && (
                <PasswordInput
                  className={cx({
                    redactedField: hideApiKeyEye
                  })}
                  autoComplete="off"
                  id="CustomServiceSearchInstanceConfig__form-password-input"
                  invalid={invalidApiKey}
                  invalidText={invalidApiKeyText}
                  placeholder={REDACTED_FIELD}
                  readOnly={readOnly}
                  labelText={formatMsg(messages.apiKeyLabelText)}
                  onChange={apiKeyChange}
                  size="md"
                  value={apiKey}
                />
              )}
            </FormGroup>
          )}
        </div>
      </div>
    </>
  );
};
